// The content contract: what the CMS API accepts and returns for each content
// type. The admin forms validate with these schemas, the API re-validates every
// write with them, and the site build types the published snapshot with them.
// DB columns are an implementation detail of the API and never appear here.

import { z } from 'zod'

/* ------------------------------------------------------------------ */
/* Building blocks                                                       */
/* ------------------------------------------------------------------ */

const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v)

/** A translatable string. `ar` is null until someone translates it; the site then falls back to `en`. */
export const L = z.object({
  en: z.string().trim().min(1, 'English text is required'),
  ar: z.preprocess(blankToNull, z.string().trim().nullish()).transform((v) => v ?? null),
})
export type L = z.output<typeof L>

/** Optional field: missing, null, or a blank string all become null. */
function opt<T extends z.ZodType>(schema: T) {
  return z.preprocess(blankToNull, schema.nullable()).default(null)
}

/** An optional translatable string: null when the English side is blank. */
const optL = z.preprocess((v) => {
  if (v == null) return null
  if (typeof v === 'object' && typeof (v as { en?: unknown }).en === 'string' && !(v as { en: string }).en.trim()) {
    return null
  }
  return v
}, L.nullable()).default(null)

const Url = z.url({ protocol: /^https?$/ })
/** A site-relative link such as /roadmaps/devops-to-mlops. */
const InternalPath = z.string().regex(/^\/[a-z0-9/_-]*$/i, 'Must start with / and contain no spaces')
/** An R2 object key returned by the media upload, e.g. team/aya-nasser-salama-1a2b3c4d.jpg */
export const MediaKey = z.string().regex(/^[a-z0-9][a-z0-9/_.-]*\.(png|jpe?g|webp)$/, 'Not a media key')
/**
 * Whether YYYY-MM-DD names a real calendar day. Date.parse is no use here: it
 * quietly rolls 2026-02-30 over to March 2nd.
 */
function realDay(s: string): boolean {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').refine(realDay, 'Not a real date')
/** ISO-8601 date-time WITH an explicit offset. Cairo is +03:00 in summer, +02:00 in winter. */
export const DateTimeWithOffset = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?[+-](0\d|1[0-4]):[0-5]\d$/,
    'Use an ISO date-time with an offset, e.g. 2026-09-06T19:00:00+03:00',
  )
  .refine(realDay, 'Not a real date')

export const Slug = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, digits and single hyphens only')

export const STATUSES = ['draft', 'published', 'archived'] as const
export const Status = z.enum(STATUSES)
export type Status = z.infer<typeof Status>

const base = {
  slug: Slug,
  status: Status.default('draft'),
}

/** Fields the API adds to every item it returns. */
export type Meta = {
  id: string
  sortOrder: number
  createdAt: string
  updatedAt: string
  updatedBy: string | null
}

/* ------------------------------------------------------------------ */
/* Content types                                                         */
/* ------------------------------------------------------------------ */

export const ACCENTS = ['teal', 'cyan', 'violet', 'amber'] as const
export const Accent = z.enum(ACCENTS)

export const PartnerInput = z.object({
  ...base,
  name: z.string().trim().min(1).max(120),
  role: L,
  blurb: optL,
  href: Url,
  logo: opt(MediaKey),
  accent: Accent,
})

export const PLATFORMS = ['LinkedIn', 'Medium'] as const
export const ArticleInput = z.object({
  ...base,
  title: L,
  description: L,
  date: IsoDate,
  platform: z.enum(PLATFORMS),
  href: Url,
  tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).default([]),
  internalHref: opt(InternalPath),
})

export const TIERS = ['founder', 'director', 'lead'] as const
export const TeamMemberInput = z.object({
  ...base,
  name: z.string().trim().min(1).max(120),
  initials: z.string().trim().min(1).max(4),
  tier: z.enum(TIERS),
  /** Lead's axis number, shown on the team page. */
  axis: opt(z.number().int().min(1).max(99)),
  role: L,
  bio: L,
  photo: opt(MediaKey),
  linkedin: opt(Url),
  email: opt(z.email()),
})

export const FAQ_SCOPES = ['general', 'course'] as const
export const FaqInput = z.object({
  ...base,
  q: L,
  a: L,
  scope: z.enum(FAQ_SCOPES),
})

export const ROADMAP_ACCENTS = ['cyan', 'amber', 'violet'] as const
export const RoadmapInput = z.object({
  ...base,
  title: L,
  tagline: L,
  level: L,
  duration: L,
  commitment: L,
  audience: L,
  accent: z.enum(ROADMAP_ACCENTS),
  published: IsoDate,
  sourceUrl: opt(Url),
  /** Markdown, English in both editions. `## Phase N — Title (when)` headings drive the timeline. */
  body: z.string().min(1),
  /** Arabic names for the phase headings, keyed by their label ("Phase 0", "Specialization 3"). */
  phaseTitlesAr: z
    .array(z.object({ label: z.string().regex(/^(Phase|Specialization) \d+$/), titleAr: z.string().trim().min(1) }))
    .default([]),
})

/** Site-wide singleton: copy that belongs to no single item. */
/** A community number: a whole count, or null to keep the site's default. */
const Count = opt(z.number().int().min(0).max(100_000_000))

export const SettingsInput = z.object({
  teamSummary: optL,
  /** The home page's community numbers (see migration 0007 for which are fallbacks). */
  statWhatsapp: Count,
  statDiscord: Count,
  statLinkedin: Count,
  statStudents: Count,
  statYtViews: Count,
  statYtSubs: Count,
})

/* ------------------------------------------------------------------ */
/* Registry                                                              */
/* ------------------------------------------------------------------ */

export const CONTENT_SCHEMAS = {
  partners: PartnerInput,
  articles: ArticleInput,
  team: TeamMemberInput,
  faqs: FaqInput,
  roadmaps: RoadmapInput,
} as const

export type ContentType = keyof typeof CONTENT_SCHEMAS
export const CONTENT_TYPES = Object.keys(CONTENT_SCHEMAS) as ContentType[]

/** What a client sends for type T. */
export type ContentInput<T extends ContentType> = z.input<(typeof CONTENT_SCHEMAS)[T]>
/** What the API returns for type T. */
export type ContentItem<T extends ContentType> = z.output<(typeof CONTENT_SCHEMAS)[T]> & Meta

export type Partner = ContentItem<'partners'>
export type Article = ContentItem<'articles'>
export type TeamMember = ContentItem<'team'>
export type Faq = ContentItem<'faqs'>
export type Roadmap = ContentItem<'roadmaps'>
export type Settings = z.output<typeof SettingsInput> & { updatedAt: string }

/** The published snapshot the site build fetches from /api/public/content. */
export type PublicContent = {
  generatedAt: string
  /** Where uploaded images are served from; join with a media key. No trailing slash. */
  mediaBaseUrl: string
  partners: Partner[]
  articles: Article[]
  team: TeamMember[]
  faqs: Faq[]
  roadmaps: Roadmap[]
  settings: Settings
}
